import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, ProgressBar, Badge, Modal } from 'react-bootstrap';
import axios from 'axios';
import { 
  FaPlus,
  FaBuilding,
  FaRobot,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaEnvelope,
  FaDollarSign,
  FaBell,
  FaEye,
  FaPaperPlane,
  FaArrowRight,
  FaMoneyBillWave,
  FaClock,
  FaHeart,
  FaTimes,
  FaTicketAlt
} from 'react-icons/fa';
import '../../../../assets/css/customer/dashboard/CustomerDashboard.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';
import Swal from 'sweetalert2';
import AppContext from '../../../../context/AppContext';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  // Modal States
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showBookVenueModal, setShowBookVenueModal] = useState(false);
  const [showAIPlannerModal, setShowAIPlannerModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showInvitationsListModal, setShowInvitationsListModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);

  // ── NEW: real counts for stats cards (My Events, Booked Venues, Invitations Sent) ──
  const [eventsCount, setEventsCount] = useState(0);
  const [bookedVenuesCount, setBookedVenuesCount] = useState(0);
  const [invitationsCount, setInvitationsCount] = useState(0);

  // ── NEW: loading state for Create Event / Book Venue buttons (same pattern as VenueCardsSection) ──
  const [navLoading, setNavLoading] = useState(null);

  // Stats Data — value now pulled from real state; Total Payments left as-is for now
  const stats = [
    {
      id: 1,
      title: 'My Events',
      value: String(eventsCount),
      icon: <FaCalendarAlt />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      bg: '#eeedfd'
    },
    {
      id: 2,
      title: 'Booked Venues',
      value: String(bookedVenuesCount),
      icon: <FaBuilding />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      bg: '#fff0f5'
    },
    // {
    //   id: 3,
    //   title: 'Invitations Sent',
    //   value: String(invitationsCount),
    //   icon: <FaEnvelope />,
    //   gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    //   bg: '#e0f7ff'
    // },
    {
      id: 4,
      title: 'Total Payments',
      value: 'PKR 450,000',
      icon: <FaDollarSign />,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      bg: '#e0fff9'
    }
  ];

  // Upcoming Event
  const upcomingEvent = {
    name: 'Sarah & Ahmed Wedding',
    date: '25 March 2026',
    venue: 'Pearl Continental',
    status: 'Confirmed'
  };

  // AI Planner Progress
  const aiProgress = {
    completion: 65,
    tasks: [
      { title: 'Venue Selected', completed: true },
      { title: 'Guest List Added', completed: true },
      { title: 'Catering Pending', completed: false }
    ]
  };

  // Invitations Summary
  const invitationsSummary = {
    sent: 120,
    accepted: 85,
    pending: 35
  };

  // Payment Summary
  const paymentSummary = {
    totalPaid: 'PKR 450,000',
    pendingDue: 'PKR 50,000'
  };

  // Recent Notifications
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    if (loggedInCustomer) {
      const formData = new FormData();
      formData.append('wecustomer_id', loggedInCustomer.id);

      axios.post(`${baseUrl}/customer/notifications/viewAllNotifications`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => {
        if (res.data.success) {
          setNotifications(res.data.notifications);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    }
  }, []);

  // Recent Tickets
  const [recentTickets, setRecentTickets] = useState([]);

  const getTicketBadgeType = (status) => {
    if (status === 'Open')    return 'danger';
    if (status === 'Pending') return 'warning';
    return 'success'; // Closed / Resolved
  };

  useEffect(() => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    if (loggedInCustomer) {
      const formData = new FormData();
      formData.append('wecustomer_id', loggedInCustomer.id);

      axios.post(`${baseUrl}/customer/helpandsupport/viewMyTickets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => {
        if (res.data.success === true) {
          const sorted = [...res.data.tickets].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setRecentTickets(sorted.slice(0, 3));
        }
      })
      .catch((err) => {
        console.log(err);
      });
    }
  }, []);

  // ── NEW: My Events count — same endpoint MyEvents.js uses ──
  useEffect(() => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    if (loggedInCustomer) {
      const formData = new FormData();
      formData.append('wecustomer_id', loggedInCustomer.id);

      axios.post(`${baseUrl}/customer/events/viewAllEvents`, formData)
        .then((res) => {
          setEventsCount(res.data.length);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // ── NEW: Booked Venues count ──
  useEffect(() => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    if (loggedInCustomer) {
      const formData = new FormData();
      formData.append('wecustomer_id', loggedInCustomer.id);

      axios.post(`${baseUrl}/customer/bookings/viewMyBookings`, formData)
        .then((res) => {
          const bookings = res.data.bookings || res.data;
          setBookedVenuesCount(Array.isArray(bookings) ? bookings.length : 0);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // ── NEW: Invitations Sent count ──
  // NOTE: this route is a GET with no customer filter param defined in api.php,
  // so wecustomer_id is passed as a query param here — confirm this matches
  // what Invitations.js already sends, and adjust the param name if it differs.
  useEffect(() => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    if (loggedInCustomer) {
      axios.get(`${baseUrl}/customer/invitation/getinvitations`, {
        params: { wecustomer_id: loggedInCustomer.id }
      })
        .then((res) => {
          const invitations = res.data.invitations || res.data;
          setInvitationsCount(Array.isArray(invitations) ? invitations.length : 0);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // Handle Button Clicks
  const handleCreateEvent = () => {
    setNavLoading('create');
    setTimeout(() => {
      navigate("/customer/my-events");
      setNavLoading(null);
    }, 600);
  };
  const handleBookVenue = () => {
    setNavLoading('book');
    setTimeout(() => {
      navigate("/customer/my-bookings");
      setNavLoading(null);
    }, 600);
  };
  const handleAIPlanner = () => navigate("/customer/ai-plannar");
  const handleViewEventDetails = () => setShowEventDetailsModal(true);
  const handleSendInvitation = () => setShowInvitationModal(true);
  const handleViewInvitations = () => setShowInvitationsListModal(true);
  const handleViewPaymentHistory = () => setShowPaymentHistoryModal(true);

  return (
    <CustomerLayout>
      <div className="cdb-customer-dashboard">
        <Container fluid className="cdb-container">
          {/* Page Header */}
          <div className="cdb-page-header">
            <div className="cdb-header-content">
              <h1 className="cdb-page-title">Customer Dashboard</h1>
              <p className="cdb-page-subtitle">Manage your wedding events, bookings & planning</p>
            </div>
          </div>

          {/* Action Buttons */}
          <Row className="cdb-action-row">
            <Col lg={12}>
              <div className="cdb-action-buttons">
                <Button className="cdb-action-btn cdb-btn-create" onClick={handleCreateEvent} disabled={navLoading === 'create'}>
                  {navLoading === 'create' ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaPlus className="cdb-btn-icon" />
                      Create Event
                    </>
                  )}
                </Button>
                <Button className="cdb-action-btn cdb-btn-book" onClick={handleBookVenue} disabled={navLoading === 'book'}>
                  {navLoading === 'book' ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaBuilding className="cdb-btn-icon" />
                      Book Venue
                    </>
                  )}
                </Button>
                {/* <Button className="cdb-action-btn cdb-btn-ai" onClick={handleAIPlanner}>
                  <FaRobot className="cdb-btn-icon" />
                  AI Plan Event
                </Button> */}
              </div>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="cdb-stats-row">
            {stats.map((stat) => (
              <Col key={stat.id} lg={3} md={6} className="cdb-stats-col">
                <Card className="cdb-stat-card" style={{ background: stat.bg }}>
                  <Card.Body className="cdb-stat-body">
                    <div className="cdb-stat-icon" style={{ background: stat.gradient }}>
                      {stat.icon}
                    </div>
                    <div className="cdb-stat-details">
                      <p className="cdb-stat-title">{stat.title}</p>
                      <h2 className="cdb-stat-value">{stat.value}</h2>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="cdb-content-row">
            {/* Left Column */}
            <Col lg={8}>
              {/* Upcoming Event */}
              <Card className="cdb-section-card cdb-upcoming-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaCalendarAlt className="cdb-section-icon" />
                    Upcoming Event
                  </h3>
                  <Badge bg="success" className="cdb-status-badge">
                    <FaCheckCircle /> {upcomingEvent.status}
                  </Badge>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-event-details">
                    <div className="cdb-event-info-row">
                      <div className="cdb-event-info-item">
                        <span className="cdb-info-label">Event Name:</span>
                        <span className="cdb-info-value">{upcomingEvent.name}</span>
                      </div>
                    </div>
                    <div className="cdb-event-info-row">
                      <div className="cdb-event-info-item">
                        <span className="cdb-info-label">
                          <FaCalendarAlt className="cdb-inline-icon" /> Date:
                        </span>
                        <span className="cdb-info-value">{upcomingEvent.date}</span>
                      </div>
                    </div>
                    <div className="cdb-event-info-row">
                      <div className="cdb-event-info-item">
                        <span className="cdb-info-label">
                          <FaMapMarkerAlt className="cdb-inline-icon" /> Venue:
                        </span>
                        <span className="cdb-info-value">{upcomingEvent.venue}</span>
                      </div>
                    </div>
                  </div>
                  <div className="cdb-event-actions">
                    <Button className="cdb-btn cdb-btn-primary" onClick={handleViewEventDetails}>
                      <FaEye className="cdb-btn-icon" />
                      View Event Details
                    </Button>
                    <Button className="cdb-btn cdb-btn-secondary" onClick={handleSendInvitation}>
                      <FaPaperPlane className="cdb-btn-icon" />
                      Send Invitation
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* AI Planner Progress - commented out */}
              {/* <Card className="cdb-section-card cdb-ai-planner-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaRobot className="cdb-section-icon" />
                    AI Planner Progress
                  </h3>
                  <span className="cdb-progress-percent">{aiProgress.completion}%</span>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-progress-section">
                    <p className="cdb-progress-label">Planning Completion</p>
                    <ProgressBar 
                      now={aiProgress.completion} 
                      className="cdb-progress-bar"
                      variant="success"
                    />
                  </div>
                  <div className="cdb-tasks-list">
                    {aiProgress.tasks.map((task, index) => (
                      <div key={index} className={`cdb-task-item ${task.completed ? 'completed' : 'pending'}`}>
                        {task.completed ? (
                          <FaCheckCircle className="cdb-task-icon cdb-icon-success" />
                        ) : (
                          <FaClock className="cdb-task-icon cdb-icon-pending" />
                        )}
                        <span className="cdb-task-title">{task.title}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="cdb-btn cdb-btn-ai" onClick={handleAIPlanner}>
                    Continue Planning
                    <FaArrowRight className="cdb-btn-icon-right" />
                  </Button>
                </Card.Body>
              </Card> */}

              {/* Recent Tickets */}
              <Card className="cdb-section-card cdb-notifications-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaTicketAlt className="cdb-section-icon" />
                    Recent Tickets
                  </h3>
                  <Button className="cdb-btn cdb-btn-outline btn-sm" onClick={() => navigate('/customer/helpandsupport')}>
                    View All
                  </Button>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-notifications-list">
                    {recentTickets.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                        No tickets found
                      </div>
                    ) : (
                      recentTickets.map((ticket) => (
                        <div key={ticket.id} className={`cdb-notification-item cdb-${getTicketBadgeType(ticket.status)}`}>
                          <div className="cdb-notification-icon">
                            <FaTicketAlt />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span className="cdb-notification-message">{ticket.subject}</span>
                              <Badge bg={getTicketBadgeType(ticket.status)}>
                                {ticket.status}
                              </Badge>
                            </div>
                            <small style={{ color: '#999' }}>{ticket.created_at?.slice(0, 10)}</small>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Invitations Summary - commented out */}
              {/* <Card className="cdb-section-card cdb-invitations-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaEnvelope className="cdb-section-icon" />
                    Invitations Summary
                  </h3>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-invitation-stats">
                    <div className="cdb-invitation-stat">
                      <div className="cdb-stat-number cdb-sent">{invitationsSummary.sent}</div>
                      <div className="cdb-stat-label">Sent</div>
                    </div>
                    <div className="cdb-invitation-stat">
                      <div className="cdb-stat-number cdb-accepted">{invitationsSummary.accepted}</div>
                      <div className="cdb-stat-label">Accepted</div>
                    </div>
                    <div className="cdb-invitation-stat">
                      <div className="cdb-stat-number cdb-pending">{invitationsSummary.pending}</div>
                      <div className="cdb-stat-label">Pending</div>
                    </div>
                  </div>
                  <Button className="cdb-btn cdb-btn-outline" onClick={handleViewInvitations}>
                    <FaEye className="cdb-btn-icon" />
                    View Invitations
                  </Button>
                </Card.Body>
              </Card> */}
            </Col>

            {/* Right Column */}
            <Col lg={4}>
              {/* Payment Summary */}
              <Card className="cdb-section-card cdb-payment-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaMoneyBillWave className="cdb-section-icon" />
                    Payment Summary
                  </h3>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-payment-info">
                    <div className="cdb-payment-item cdb-paid">
                      <span className="cdb-payment-label">Total Paid</span>
                      <span className="cdb-payment-value">{paymentSummary.totalPaid}</span>
                    </div>
                    <div className="cdb-payment-item cdb-due">
                      <span className="cdb-payment-label">Pending Due</span>
                      <span className="cdb-payment-value">{paymentSummary.pendingDue}</span>
                    </div>
                  </div>
                  <Button className="cdb-btn cdb-btn-payment" onClick={handleViewPaymentHistory}>
                    <FaDollarSign className="cdb-btn-icon" />
                    View Payment History
                  </Button>
                </Card.Body>
              </Card>

              {/* Recent Notifications */}
              <Card className="cdb-section-card cdb-notifications-card">
                <Card.Header className="cdb-card-header">
                  <h3 className="cdb-section-title">
                    <FaBell className="cdb-section-icon" />
                    Recent Notifications
                  </h3>
                </Card.Header>
                <Card.Body className="cdb-card-body">
                  <div className="cdb-notifications-list">
                    {notifications.map((notification) => (
                      <div key={notification.id} className={`cdb-notification-item cdb-${notification.status === 'unread' ? 'warning' : 'info'}`}>
                        <div className="cdb-notification-icon">
                          <FaBell />
                        </div>
                        <div style={{ flex: 1 }}>
                          <strong className="cdb-notification-message">{notification.title}</strong>
                          <br />
                          <span className="cdb-notification-message">{notification.descrption}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modals */}

        {/* Event Details Modal */}
        <Modal show={showEventDetailsModal} onHide={() => setShowEventDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="cdb-modal-header">
            <Modal.Title className="cdb-modal-title">
              <FaEye /> Event Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="cdb-modal-body">
            <h5>{upcomingEvent.name}</h5>
            <p><strong>Date:</strong> {upcomingEvent.date}</p>
            <p><strong>Venue:</strong> {upcomingEvent.venue}</p>
            <p><strong>Status:</strong> {upcomingEvent.status}</p>
          </Modal.Body>
        </Modal>

        {/* Send Invitation Modal */}
        <Modal show={showInvitationModal} onHide={() => setShowInvitationModal(false)} size="lg" centered>
          <Modal.Header closeButton className="cdb-modal-header">
            <Modal.Title className="cdb-modal-title">
              <FaPaperPlane /> Send Invitation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="cdb-modal-body">
            <p>Invitation sending form will be here...</p>
          </Modal.Body>
        </Modal>

        {/* Invitations List Modal */}
        <Modal show={showInvitationsListModal} onHide={() => setShowInvitationsListModal(false)} size="lg" centered>
          <Modal.Header closeButton className="cdb-modal-header">
            <Modal.Title className="cdb-modal-title">
              <FaEnvelope /> All Invitations
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="cdb-modal-body">
            <p>Sent: {invitationsSummary.sent}</p>
            <p>Accepted: {invitationsSummary.accepted}</p>
            <p>Pending: {invitationsSummary.pending}</p>
          </Modal.Body>
        </Modal>

        {/* Payment History Modal */}
        <Modal show={showPaymentHistoryModal} onHide={() => setShowPaymentHistoryModal(false)} size="lg" centered>
          <Modal.Header closeButton className="cdb-modal-header">
            <Modal.Title className="cdb-modal-title">
              <FaDollarSign /> Payment History
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="cdb-modal-body">
            <p><strong>Total Paid:</strong> {paymentSummary.totalPaid}</p>
            <p><strong>Pending Due:</strong> {paymentSummary.pendingDue}</p>
          </Modal.Body>
        </Modal>
      </div>
    </CustomerLayout>
  );
};

export default CustomerDashboard;